// The app's data, generated from .appfactory/product-plan.json.
//
// Regenerated by `appfactory product apply`. Edit the PLAN and re-apply
// rather than editing here, or the next apply will disagree with you.
//
// Deliberately the same shape the chassis shipped: plain `ref`s in a module,
// seeded through `demoData` so /demo shows rows and the real app starts
// empty. A generated app should not introduce a state library its author
// never asked for — and `demoData` is what makes the QA `empty` fixture
// actually produce an empty screen rather than a populated one.

import { ref, computed, watch, toRaw } from 'vue';
import { demoData } from './core/demo.js';
import { records } from './core/storage.js';

// Rows survive a reload.
//
// `core/storage.js` is an IndexedDB record store the chassis has always
// shipped, and the generated store used to ignore it completely — a plain
// `ref([])` that lost everything the moment the page reloaded. An app that
// cannot keep what you typed into it is not working, however well it renders.
//
// Demo rows are NOT persisted. They have negative ids and exist for /demo and
// for QA fixtures; writing them to the real store would put sample customers
// in somebody's actual queue.
const persist = (row) => (Number(row?.id) > 0 ? records.put(toRaw(row)) : null);

// ONE id sequence for every entity, because there is one store.
//
// `records` is a single IndexedDB object store keyed on `id`. Giving each
// entity its own counter starting at 1 meant the first order written
// overwrote the first customer, and removeOrder(1) deleted them — silent
// cross-entity data loss that only appears once an app has two entities.
//
// Seeded from the highest id already stored, so it also survives a reload:
// restarting at 1 would overwrite the first row ever added.
let seq = 0;
const nextId = () => (seq += 1);

// Everything hydrates from ONE read, and nothing may allocate an id before
// it finishes. A row added while this was still in flight took id 1,
// overwrote a stored record, and was then dropped from the screen when the
// hydrated rows replaced the list.
const ready = records.all().then((rows) => {
  seq = rows.reduce((n, r) => Math.max(n, Number(r.id) || 0), 0);
  return rows;
}).catch(() => []);   // no IndexedDB (SSR, private mode) — stay in memory

/** Resolves once stored rows are loaded. Await it before writing. */
export const storeReady = ready;

/** One page the machine has produced. Stores the RECIPE, not the render — source, seed and settings — so a fax re-renders identically forever and the outbox costs kilobytes instead of megabytes. */
export const faxs = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededFax = demoData([
  { id: -1, kind: 'photo', toName: 'Sample toName', fromName: 'Sample fromName', subject: 'Sample subject', memo: 'A short memo for Fax 1, long enough to show wrapping.', source: 'Sample source', thumb: 'Sample thumb', seed: 1, condition: 'mint', pages: 1, status: 'draft', createdAt: new Date(Date.now() - 1 * 11 * 60 * 1000).toISOString(), sentAt: new Date(Date.now() - 1 * 11 * 60 * 1000).toISOString() },
  { id: -2, kind: 'screenshot', toName: 'Sample toName', fromName: 'Sample fromName', subject: 'Sample subject', memo: 'A short memo for Fax 2, long enough to show wrapping.', source: 'Sample source', thumb: 'Sample thumb', seed: 2, condition: 'used', pages: 2, status: 'held', createdAt: new Date(Date.now() - 2 * 11 * 60 * 1000).toISOString(), sentAt: new Date(Date.now() - 2 * 11 * 60 * 1000).toISOString() },
  { id: -3, kind: 'memo', toName: 'Sample toName', fromName: 'Sample fromName', subject: 'Sample subject', memo: 'A short memo for Fax 3, long enough to show wrapping.', source: 'Sample source', thumb: 'Sample thumb', seed: 3, condition: 'haunted', pages: 3, status: 'sent', createdAt: new Date(Date.now() - 3 * 11 * 60 * 1000).toISOString(), sentAt: new Date(Date.now() - 3 * 11 * 60 * 1000).toISOString() },
]);
watch(seededFax, (rows) => {
  if (rows.length && !faxs.value.length) faxs.value = [...rows];
}, { immediate: true });

export const faxsReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'fax');
  if (mine.length) faxs.value = mine;
  return faxs.value;
});

export function addFax(input = {}) {
  const extra = { ...input };
  delete extra.id; delete extra.entity;
  const row = { ...extra, id: nextId(), entity: 'fax',
    kind: input.kind ?? '',
    toName: input.toName ?? '',
    fromName: input.fromName ?? '',
    subject: input.subject ?? '',
    memo: input.memo ?? '',
    source: input.source ?? '',
    thumb: input.thumb ?? '',
    seed: input.seed ?? 0,
    condition: input.condition ?? '',
    pages: input.pages ?? 0,
    status: input.status ?? '',
    createdAt: input.createdAt ?? new Date().toISOString(),
    sentAt: input.sentAt ?? new Date().toISOString(),
  };
  faxs.value = [row, ...faxs.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveFax(row) {
  faxs.value = faxs.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeFax(id) {
  faxs.value = faxs.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findFax = (id) =>
  faxs.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentFax = computed(() => faxs.value.slice(0, 3));
export const faxsTitle = (row) => row?.toName;

/** The unit itself, and its moods. One row. Page count, toner and jam state are what give the prop a life independent of the user. */
export const machines = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededMachine = demoData([
  { id: -1, tsi: 'Sample tsi', number: 'Sample number', dateFormat: 'mdy', pageCount: 1, toner: 1, jammed: true, speaker: true, largeControls: true, plainEnglish: true, toldOnce: true },
  { id: -2, tsi: 'Sample tsi', number: 'Sample number', dateFormat: 'dmy', pageCount: 2, toner: 2, jammed: false, speaker: false, largeControls: false, plainEnglish: false, toldOnce: false },
  { id: -3, tsi: 'Sample tsi', number: 'Sample number', dateFormat: 'mdy', pageCount: 3, toner: 3, jammed: false, speaker: false, largeControls: false, plainEnglish: false, toldOnce: false },
]);
watch(seededMachine, (rows) => {
  if (rows.length && !machines.value.length) machines.value = [...rows];
}, { immediate: true });

export const machinesReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'machine');
  if (mine.length) machines.value = mine;
  return machines.value;
});

export function addMachine(input = {}) {
  const extra = { ...input };
  delete extra.id; delete extra.entity;
  const row = { ...extra, id: nextId(), entity: 'machine',
    tsi: input.tsi ?? '',
    number: input.number ?? '',
    dateFormat: input.dateFormat ?? '',
    pageCount: input.pageCount ?? 0,
    toner: input.toner ?? 0,
    jammed: input.jammed ?? false,
    speaker: input.speaker ?? false,
    largeControls: input.largeControls ?? false,
    plainEnglish: input.plainEnglish ?? false,
    toldOnce: input.toldOnce ?? false,
  };
  machines.value = [row, ...machines.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveMachine(row) {
  machines.value = machines.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeMachine(id) {
  machines.value = machines.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findMachine = (id) =>
  machines.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentMachine = computed(() => machines.value.slice(0, 3));
export const machinesTitle = (row) => row?.tsi;
